<div *ngIf="loading" class="custom-loader-overlay">
    <div class="custom-loader-container">
        <div class="custom-loader"></div>
        <div class="loader-message">
            <div>{{ loaderMessageLine1 }}</div>
            <div>{{ loaderMessageLine2 }}</div>
        </div>
    </div>
</div>

<div class="v2-shell">
    <div class="sticky-toolbar text-[14px]">
        <p-toolbar class="text-[14px] p-0">
            <ng-template pTemplate="start">
                <div class="left-buttons flex flex-wrap gap-2">
                    <p-button
                        [label]="saveOrderButtonLabel"
                        *ngIf="!readOnly && !isReadOnly"
                        icon="pi pi-check"
                        severity="secondary"
                        (onClick)="submitOrder()"
                        [disabled]="isTableInEditMode || loading || orderSaveInFlight || isSaveBlockedByStock"
                        [pTooltip]="saveButtonStockBlockTooltip"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        *ngIf="!readOnly && !isReadOnly"
                        label="Excel Upload"
                        icon="pi pi-file-excel"
                        severity="secondary"
                        (onClick)="openExcelImportDialog()"
                        [disabled]="!canUploadExcel"
                        pTooltip="Upload articles from Excel or download template"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        label="Download Lines"
                        icon="pi pi-download"
                        severity="secondary"
                        (onClick)="downloadOrderLines()"
                        [disabled]="!canDownloadOrderLines"
                        pTooltip="Download order lines as Excel"
                        tooltipPosition="top"
                    ></p-button>
                    <input
                        #excelFileInput
                        type="file"
                        accept=".xlsx,.xls"
                        class="hidden"
                        (change)="onExcelFileSelected($event)"
                    />
                    <p-button
                        *ngIf="showEditPricesButton"
                        [label]="priceEditMode ? 'Done' : 'Edit Prices'"
                        icon="pi pi-pencil"
                        [outlined]="!priceEditMode"
                        severity="secondary"
                        (onClick)="togglePriceEditMode()"
                        [disabled]="updatePricesInFlight"
                        [pTooltip]="getEditPricesTooltip()"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        *ngIf="showUpdatePricesButton"
                        label="Update Prices"
                        icon="pi pi-dollar"
                        severity="secondary"
                        (onClick)="updatePricesInM3()"
                        [disabled]="!canUpdatePricesInM3()"
                        [pTooltip]="getUpdatePricesTooltip()"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        label="Browse Item"
                        icon="pi pi-search"
                        severity="secondary"
                        (onClick)="openItemBrowsePopup()"
                        [disabled]="!canOpenItemBrowsePopup"
                        [pTooltip]="itemBrowseTooltip"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        label="View lots"
                        icon="pi pi-box"
                        severity="secondary"
                        (onClick)="onViewLots()"
                        pTooltip="View lot, expiry and stock for any item"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        label="Price history"
                        icon="pi pi-history"
                        severity="secondary"
                        (onClick)="openOldSalesPricePopup()"
                        [disabled]="!oldSalesPriceCustomer"
                        [pTooltip]="oldSalesPriceCustomer ? 'See price history for this customer' : 'Select a customer first'"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        *ngIf="!readOnly && !isReadOnly"
                        [label]="linesBulkEdit ? 'Done' : 'Edit'"
                        icon="pi pi-pencil"
                        [outlined]="!linesBulkEdit"
                        severity="secondary"
                        (onClick)="onToggleBulkEdit()"
                    ></p-button>
                    <p-button
                        *ngIf="showPickingListButton"
                        label="Picking List"
                        icon="pi pi-list"
                        severity="secondary"
                        (onClick)="openPickingList()"
                        [disabled]="!item?.orderNumber || item?.orderNumber === 'New Order'"
                    >
                    </p-button>
                    <p-button
                        *ngIf="item && ((isReadOnly && item.orderMovingStatus === 'INV') || !isReadOnly)"
                        label="Invoice"
                        icon="pi pi-file"
                        severity="secondary"
                        (onClick)="showInvoiceConfirm()"
                        [disabled]="!canGenerateInvoiceForOrder()"
                        [pTooltip]="getInvoiceTooltip()"
                        tooltipPosition="top"
                    >
                    </p-button>
                    <p-button
                        label="Refresh line status"
                        icon="pi pi-sync"
                        severity="secondary"
                        (onClick)="refreshM3LineStatuses()"
                        [disabled]="!canRefreshM3LineStatuses()"
                        [pTooltip]="getRefreshM3LineStatusesTooltip()"
                        tooltipPosition="top"
                    ></p-button>
                    <p-button
                        *ngIf="isManagerOrSuperUser"
                        label="AR Statements"
                        icon="pi pi-envelope"
                        severity="secondary"
                        class="mr-2"
                        (onClick)="openARStatementDialog()"
                    />
                </div>
            </ng-template>

            <ng-template pTemplate="end">
                <div class="right-buttons flex flex-wrap gap-2">
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" (onClick)="goBack()"></p-button>
                </div>
            </ng-template>
        </p-toolbar>
    </div>

    <div *ngIf="isCreditNoteOrder" class="credit-note-banner w-full mb-4" role="alert">
        CREDIT NOTE ORDER — quantities will be recorded as negative returns.
    </div>

    <div *ngIf="overdueRestrictionBanner as od" class="credit-limit-exceeded-banner mb-4" role="alert" aria-live="polite">
        <div class="credit-limit-exceeded-banner__title">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Overdue Days (Credit Limit 4)</span>
        </div>
        <div class="credit-limit-exceeded-banner__metrics">
            <div class="credit-limit-exceeded-banner__metric">
                <i class="pi pi-calendar" aria-hidden="true"></i>
                <span>Overdue days: <strong>{{ od.nood }}</strong> / limit <strong>{{ od.odud }}</strong></span>
            </div>
        </div>
    </div>

    <div *ngIf="creditExposureBanner as exp" class="credit-limit-exceeded-banner mb-4" role="alert" aria-live="polite">
        <div class="credit-limit-exceeded-banner__title">
            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
            <span>Credit Limit Exceeded</span>
        </div>
        <div class="credit-limit-exceeded-banner__metrics">
            <div class="credit-limit-exceeded-banner__metric">
                <i class="pi pi-wallet" aria-hidden="true"></i>
                <span>Outstanding: <strong>{{ exp.outstanding | number: '1.2-2' }}</strong></span>
            </div>
            <span class="credit-limit-exceeded-banner__divider" aria-hidden="true"></span>
            <div class="credit-limit-exceeded-banner__metric">
                <i class="pi pi-shopping-cart" aria-hidden="true"></i>
                <span>Order value: <strong>{{ exp.orderValue | number: '1.2-2' }}</strong></span>
            </div>
            <span class="credit-limit-exceeded-banner__divider" aria-hidden="true"></span>
            <div class="credit-limit-exceeded-banner__metric">
                <i class="pi pi-credit-card" aria-hidden="true"></i>
                <span>Credit limit: <strong>{{ exp.creditLimit3 | number: '1.2-2' }}</strong></span>
            </div>
            <span class="credit-limit-exceeded-banner__divider" aria-hidden="true"></span>
            <div class="credit-limit-exceeded-banner__metric">
                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                <span>Exceeded by: <strong>{{ exp.exceedBy | number: '1.2-2' }}</strong></span>
            </div>
        </div>
    </div>

    <div class="v2-page">
        <div class="v2-layout">
            <div class="v2-main-col">
                <app-v2-recommendations
                    *ngIf="showRecommendationsPanel || recommendationsLoading || orderRecommendations.length"
                    [recommendations]="orderRecommendations"
                    [loading]="recommendationsLoading"
                    [stockByItemId]="recommendationStockByItemId"
                    [currencySymbol]="currencySymbol"
                    (select)="onRecommendationSelect($event)"
                ></app-v2-recommendations>

                <app-v2-add-row
                    *ngIf="!readOnly && !isReadOnly && hasValidCustomerId"
                    [isCreditNoteOrder]="isCreditNoteOrder"
                    [itemForm]="itemForm"
                    [filteredItemNames]="filteredItemNames"
                    [division]="division"
                    [addDisabled]="isAddingItem"
                    [errorMessages]="errorMessages"
                    (itemSelect)="onItemNameSelect($event)"
                    (itemInputChange)="onV2ItemInputChange($event)"
                    (restrictDecimals)="onV2RestrictDecimals($event)"
                    (quantityEnter)="onV2QuantityEnter()"
                    (rateEnter)="onRateEnter()"
                    (rateBlur)="onV2RateBlur()"
                    (discountEnter)="onDiscountEnter()"
                    (discountBlur)="onDiscountEnter()"
                    (addItem)="submitItem()"
                    (cancelItem)="clearItemForm()"
                    (formKeyDown)="onFormKeyDown($event)"
                ></app-v2-add-row>

                <app-v2-lines-grid
                    #linesGrid
                    [isCreditNoteOrder]="isCreditNoteOrder"
                    [itemList]="itemList"
                    [orderNumber]="item?.orderNumber || ''"
                    [orderMovingStatus]="item?.orderMovingStatus || ''"
                    [division]="division"
                    [company]="company"
                    [warehouse]="warehouse"
                    [customerId]="item?.customerId || item?.customer || ''"
                    [defaultPriceList]="product?.defaultPriceList || item?.defaultPriceList || ''"
                    [deletedItems]="deletedItems"
                    [isManagerOrSuperUser]="isManagerOrSuperUser"
                    [readOnly]="readOnly"
                    [isReadOnly]="isReadOnly"
                    [hasReferenceOrder]="hasM3ReferenceOrder()"
                    [priceEditMode]="priceEditMode"
                    [orderPriceEditable]="orderPriceEditable"
                    [selectedLineKey]="selectedLineKey"
                    (lineSelectedForInfo)="onOrderLineSelectedForInfo($event)"
                    (itemListChange)="onItemListChange($event)"
                    (summaryChange)="onSummaryChange($event)"
                    (rateBlur)="onRateBlurFromChild($event)"
                    (discountBlur)="onDiscountBlurFromChild($event)"
                    (editingStateChange)="onEditingStateChange($event)"
                    (bulkEditChange)="linesBulkEdit = $event"
                    (deleteRow)="onV2DeleteRow($event)"
                    (deleteByItemListIndex)="deleteItem($event)"
                    (marginValidationFailed)="onLineMarginValidationFailed($event)"
                ></app-v2-lines-grid>
            </div>

            <div class="v2-sidebar-col">
                <app-header-info
                    [item]="item"
                    [product]="product"
                    [readOnly]="readOnly"
                    [company]="company"
                    [division]="division"
                    [isReadOnly]="isReadOnly"
                    [userRoles]="userRoles"
                    [orderTypeOptions]="orderTypesForHeader"
                    layoutVariant="v2"
                    [sectionCollapseEnabled]="true"
                    (addressChanged)="onHeaderAddressChanged()"
                ></app-header-info>

                <app-v2-sidebar
                    [info]="info"
                    [priceLists]="priceLists"
                    [infoContext]="sidebarInfoContext"
                    [selectedItemName]="sidebarSelectedItemName"
                    [selectedItemNumber]="sidebarSelectedItemNumber"
                    [availableQty]="sidebarAvailableQty"
                    [itemCount]="itemList.length || 0"
                    [grossValue]="getTotalValue()"
                    [vatAmount]="getTotalVatAmount()"
                    [totalWeight]="getTotalWeight()"
                    [grandTotal]="getSubscription()"
                    [currencySymbol]="currencySymbol"
                ></app-v2-sidebar>
            </div>
        </div>
    </div>
</div>

<app-item-stock-lots-popup
    [visible]="showStockLotsPopup"
    (visibleChange)="showStockLotsPopup = $event"
    [itemNo]="stockLotsItemNo"
    [warehouse]="warehouse"
    [company]="company"
    [division]="division"
></app-item-stock-lots-popup>

<app-old-sales-price-popup
    [visible]="showOldSalesPricePopup"
    (visibleChange)="showOldSalesPricePopup = $event"
    [customer]="oldSalesPriceCustomer"
    [division]="oldSalesPriceDivision"
    [warehouse]="oldSalesPriceWarehouse"
    [company]="company"
    [itemNo]="oldSalesPriceItemNo"
></app-old-sales-price-popup>

<app-item-browse-popup
    [visible]="showItemBrowsePopup"
    (visibleChange)="showItemBrowsePopup = $event"
    [searchText]="itemBrowseSearch"
    (searchTextChange)="itemBrowseSearch = $event"
    [selectedRow]="selectedBrowseRow"
    [cono]="company"
    [divi]="division"
    [warehouse]="warehouse"
    [itemList]="itemList"
    (rowSelected)="onBrowseRowSelected($event)"
    (selected)="onBrowseSelect($event)"
></app-item-browse-popup>

<p-dialog
    *ngIf="showMissingCustomerBanner"
    [visible]="true"
    [modal]="true"
    [closable]="false"
    [closeOnEscape]="false"
    [dismissableMask]="false"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '28rem', maxWidth: '95vw' }"
    header="Customer Required"
>
    <ng-template pTemplate="content">
        <div class="text-center">
            <i class="pi pi-exclamation-triangle text-4xl text-orange-500 mb-3" aria-hidden="true"></i>
            <p class="m-0 text-base leading-relaxed">
                Customer number is missing for this order. You cannot add lines or save here.
                Go back to Manage Orders, select the customer again, then start the order.
            </p>
        </div>
    </ng-template>
    <ng-template pTemplate="footer">
        <p-button label="Back" icon="pi pi-arrow-left" (onClick)="onMissingCustomerGoBack()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog [(visible)]="confirmDialogVisible" [style]="{ width: '450px' }" header="Confirm Submission" [modal]="true">
    <ng-template pTemplate="content">
        <div class="text-center">
            <p>Are you sure you want to submit the header and lines?</p>
        </div>
    </ng-template>
    <ng-template pTemplate="footer">
        <p-button label="Submit" icon="pi pi-check" (click)="submitOrder(); confirmDialogVisible = false" [disabled]="loading || orderSaveInFlight || isSaveBlockedByStock" />
    </ng-template>
</p-dialog>

<app-ar-statement-form
    [(visible)]="customerDialogVisible"
    [prefillCustomer]="{
        customerNumber: selectedCustomerNumber,
        customerName: selectedCustomerName
    }"
    (submitAR)="handleARSubmit($event)"
>
</app-ar-statement-form>

<p-dialog [(visible)]="confirmInvoiceDialogVisible" [style]="{ width: '400px' }" header="Confirm Invoice" [modal]="true">
    <ng-template pTemplate="content">
        <div class="text-center">
            <p>
                Printing invoice will change the order status to <b>INV</b>.<br />
                Are you sure you want to continue?
            </p>
        </div>
    </ng-template>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" text (click)="confirmInvoiceDialogVisible = false"></p-button>
        <p-button label="OK" icon="pi pi-check" (click)="confirmInvoiceDialogVisible = false; generateInvoice()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="creditLimitDialogVisible"
    [modal]="true"
    [style]="{ width: '26rem' }"
    styleClass="credit-limit-dialog"
    [closable]="true"
    (onHide)="onCreditLimitConfirmCancel()"
>
    <ng-template pTemplate="header">
        <span *ngIf="creditLimitDialogKind === 'overdue'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Overdue Balance</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'both'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Credit Limit 3 &amp; 4 Exceeded</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'exposure'" class="credit-limit-dialog-header credit-limit-dialog-header--warning">
            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
            <span>Credit Limit Warning</span>
        </span>
    </ng-template>
    <app-credit-limit-dialog-content
        [kind]="creditLimitDialogKind"
        [data]="creditLimitDialogData"
        [nood]="creditLimitDialogNood"
        [odud]="creditLimitDialogOdud"
        [note]="creditLimitSaveNote"
    ></app-credit-limit-dialog-content>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" (onClick)="onCreditLimitConfirmCancel()"></p-button>
        <p-button label="Yes, Save" icon="pi pi-check" (onClick)="onCreditLimitConfirmAccept()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="creditLimitProceedDialogVisible"
    [modal]="true"
    [style]="{ width: '26rem' }"
    styleClass="credit-limit-dialog"
    [closable]="true"
    (onHide)="onCreditLimitProceedCancel()"
>
    <ng-template pTemplate="header">
        <span *ngIf="creditLimitDialogKind === 'overdue'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Overdue Balance</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'both'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Credit Limit 3 &amp; 4 Exceeded</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'exposure'" class="credit-limit-dialog-header credit-limit-dialog-header--warning">
            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
            <span>Credit Limit Warning</span>
        </span>
    </ng-template>
    <app-credit-limit-dialog-content
        [kind]="creditLimitDialogKind"
        [data]="creditLimitDialogData"
        [nood]="creditLimitDialogNood"
        [odud]="creditLimitDialogOdud"
        [note]="creditLimitProceedNote"
    ></app-credit-limit-dialog-content>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" (onClick)="onCreditLimitProceedCancel()"></p-button>
        <p-button [label]="creditLimitProceedLabel" icon="pi pi-arrow-right" (onClick)="onCreditLimitProceedAccept()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="marginItemWarningDialogVisible"
    [style]="{ width: '440px', maxWidth: '95vw' }"
    styleClass="margin-validation-dialog margin-item-warning-dialog"
    [modal]="true"
    [closable]="true"
    (onHide)="onMarginItemWarningDialogClose()"
>
    <ng-template pTemplate="header">
        <span class="margin-dialog-header">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Margin Below Minimum</span>
        </span>
    </ng-template>
    <app-margin-approval-dialog-content
        mode="item-warning"
        [itemName]="marginItemWarningItemName"
        [actualMargin]="marginItemWarningActual"
        [requiredMargin]="marginItemWarningRequired"
    ></app-margin-approval-dialog-content>
    <ng-template pTemplate="footer">
        <p-button
            label="OK"
            icon="pi pi-check"
            styleClass="margin-dialog-ok-btn"
            (onClick)="onMarginItemWarningDialogClose()"
        ></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="marginApprovalProceedDialogVisible"
    [style]="{ width: '440px', maxWidth: '95vw' }"
    styleClass="margin-validation-dialog margin-approval-dialog"
    [modal]="true"
    [closable]="true"
    (onHide)="onMarginApprovalProceedCancel()"
>
    <ng-template pTemplate="header">
        <span class="margin-dialog-header">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Margin Approval Required</span>
        </span>
    </ng-template>
    <app-margin-approval-dialog-content
        mode="approval"
        [failingLines]="marginApprovalSummary?.failingLines ?? []"
        [orderViolation]="marginApprovalSummary?.orderViolation ?? null"
        [note]="marginApprovalContactNote"
        [contactEmails]="marginApprovalContactEmails"
        [proceedActionLabel]="marginApprovalProceedLabel"
        [proceedNote]="marginApprovalProceedNote"
    ></app-margin-approval-dialog-content>
    <div *ngIf="marginApprovalProceedNeedsApi" class="credit-limit-approval-reason mt-3">
        <label for="marginApprovalReasonV2" class="block mb-2 font-medium">Approval reason (required)</label>
        <textarea
            id="marginApprovalReasonV2"
            pTextarea
            rows="3"
            class="w-full"
            [(ngModel)]="marginApprovalReason"
            [disabled]="marginApprovalSubmitting"
            placeholder="Enter reason for approving margin exception"
        ></textarea>
        <small class="text-color-secondary">Minimum 5 characters.</small>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [disabled]="marginApprovalSubmitting" (onClick)="onMarginApprovalProceedCancel()"></p-button>
        <p-button
            [label]="marginApprovalProceedLabel"
            icon="pi pi-check"
            styleClass="margin-dialog-ok-btn"
            [loading]="marginApprovalSubmitting"
            [disabled]="marginApprovalProceedNeedsApi && !canSubmitMarginApproval"
            (onClick)="onMarginApprovalProceedAccept()"
        ></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="marginApprovalBlockedDialogVisible"
    [style]="{ width: '440px', maxWidth: '95vw' }"
    styleClass="margin-validation-dialog margin-blocked-approval-dialog"
    [modal]="true"
    [closable]="true"
    (onHide)="onMarginApprovalBlockedDialogDismiss()"
>
    <ng-template pTemplate="header">
        <span class="margin-dialog-header">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Approval Required</span>
        </span>
    </ng-template>
    <app-margin-approval-dialog-content
        mode="blocked"
        [failingLines]="marginApprovalSummary?.failingLines ?? []"
        [orderViolation]="marginApprovalSummary?.orderViolation ?? null"
        [note]="marginApprovalContactNote"
        [contactEmails]="marginApprovalContactEmails"
        [blockedApprovalNotice]="marginApprovalBlockedNotice"
    ></app-margin-approval-dialog-content>
    <ng-template pTemplate="footer">
        <p-button
            label="OK"
            icon="pi pi-check"
            styleClass="margin-dialog-ok-btn"
            (onClick)="onMarginApprovalBlockedDialogOk()"
        ></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="orderMarginDialogVisible"
    [style]="{ width: '400px' }"
    styleClass="margin-validation-dialog"
    [modal]="true"
    [closable]="true"
    (onHide)="onOrderMarginDialogClose()"
>
    <ng-template pTemplate="header">
        <span class="margin-dialog-header">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Cannot Save Order</span>
        </span>
    </ng-template>
    <app-margin-dialog-content
        kind="order"
        [actualMargin]="orderMarginDialogActual"
        [requiredMargin]="orderMarginDialogRequired"
    ></app-margin-dialog-content>
    <ng-template pTemplate="footer">
        <p-button
            label="OK"
            icon="pi pi-check"
            styleClass="margin-dialog-ok-btn"
            (onClick)="onOrderMarginDialogClose()"
        ></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="creditLimitBlockedDialogVisible"
    [modal]="true"
    [style]="{ width: '26rem' }"
    styleClass="credit-limit-dialog"
    [closable]="true"
    (onHide)="onCreditLimitBlockedDialogClose()"
>
    <ng-template pTemplate="header">
        <span *ngIf="creditLimitDialogKind === 'overdue'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Overdue Balance</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'both'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Credit Limit 3 &amp; 4 Exceeded</span>
        </span>
        <span *ngIf="creditLimitDialogKind === 'exposure'" class="credit-limit-dialog-header credit-limit-dialog-header--exceeded">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <span>Credit Limit Exceeded</span>
        </span>
    </ng-template>
    <app-credit-limit-dialog-content
        [kind]="creditLimitDialogKind"
        [data]="creditLimitDialogData"
        [nood]="creditLimitDialogNood"
        [odud]="creditLimitDialogOdud"
        [note]="creditLimitBlockedNote"
    ></app-credit-limit-dialog-content>
    <ng-template pTemplate="footer">
        <p-button label="OK" icon="pi pi-check" (onClick)="onCreditLimitBlockedDialogClose()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="showExcelImportDialog"
    [modal]="true"
    [showHeader]="false"
    [closable]="false"
    styleClass="excel-import-dialog"
    [style]="{ width: '46rem', maxWidth: '95vw' }"
    [dismissableMask]="true"
>
    <div class="excel-import-dialog__shell">
        <button
            type="button"
            class="excel-import-dialog__close"
            aria-label="Close"
            (click)="showExcelImportDialog = false"
        >
            <i class="pi pi-times" aria-hidden="true"></i>
        </button>

        <div class="excel-import-dialog__header">
            <div class="excel-import-dialog__icon" aria-hidden="true">
                <i class="pi pi-file-excel"></i>
            </div>
            <div class="excel-import-dialog__intro">
                <h2 class="excel-import-dialog__title">Excel import</h2>
                <p class="excel-import-dialog__description">
                    Download the template or upload a filled Excel file. Columns: Item No (ITNO) mandatory, Quantity (ORQT) mandatory, Sales Price (SAPR).
                </p>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="excel-import-dialog-footer">
            <button
                type="button"
                class="excel-import-dialog__btn excel-import-dialog__btn--secondary"
                (click)="showExcelImportDialog = false"
            >
                <i class="pi pi-times" aria-hidden="true"></i>
                <span>Cancel</span>
            </button>
            <button
                type="button"
                class="excel-import-dialog__btn excel-import-dialog__btn--secondary"
                (click)="onExcelImportDownload()"
            >
                <i class="pi pi-download" aria-hidden="true"></i>
                <span>Download template</span>
            </button>
            <button
                type="button"
                class="excel-import-dialog__btn excel-import-dialog__btn--primary"
                (click)="onExcelImportUpload()"
            >
                <i class="pi pi-upload" aria-hidden="true"></i>
                <span>Upload file</span>
            </button>
        </div>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="showExcelSkippedDialog"
    header="Skipped / rejected Excel rows"
    [modal]="true"
    [style]="{ width: '36rem', maxWidth: '95vw' }"
    [dismissableMask]="true"
>
    <p *ngIf="!excelSkippedRows.length" class="m-0">No skipped rows.</p>
    <ul *ngIf="excelSkippedRows.length" class="excel-skipped-list m-0 pl-5">
        <li *ngFor="let row of excelSkippedRows" class="mb-2">
            <strong>Row {{ row.excelRow }}</strong>
            <span *ngIf="row.itno"> — {{ row.itno }}</span>: {{ row.reason }}
        </li>
    </ul>
    <ng-template pTemplate="footer">
        <p-button label="Close" icon="pi pi-times" (onClick)="showExcelSkippedDialog = false"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="wh101OrderTypeWarningVisible"
    [modal]="true"
    [style]="{ width: '26rem', maxWidth: '95vw' }"
    header="Order type warning"
    [closable]="true"
    (onHide)="onWh101OrderTypeWarningOk()"
>
    <div style="font-size: 15px; line-height: 1.45; word-break: break-word;">
        {{ wh101OrderTypeWarningMessage }}
    </div>
    <ng-template pTemplate="footer">
        <p-button label="OK" icon="pi pi-check" (onClick)="onWh101OrderTypeWarningOk()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog
    [(visible)]="syncAfterDraftDialogVisible"
    [modal]="true"
    [showHeader]="false"
    [closable]="false"
    styleClass="sync-after-draft-dialog"
    [style]="{ width: '28rem', maxWidth: '95vw' }"
    [dismissableMask]="false"
    (onHide)="onSyncAfterDraftDialogHide()"
>
    <div class="sync-after-draft-dialog__shell">
        <button
            type="button"
            class="sync-after-draft-dialog__close"
            aria-label="Close"
            (click)="onSyncAfterDraftDecline()"
        >
            <i class="pi pi-times" aria-hidden="true"></i>
        </button>

        <div class="sync-after-draft-dialog__header">
            <div class="sync-after-draft-dialog__icon" aria-hidden="true">
                <i class="pi pi-cloud-upload"></i>
            </div>
            <div class="sync-after-draft-dialog__intro">
                <h2 class="sync-after-draft-dialog__title">Order saved</h2>
                <p class="sync-after-draft-dialog__description">
                    Your draft is ready. Sync it to M3 now, or continue later from the Orders page.
                </p>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="sync-after-draft-dialog-footer">
            <button
                type="button"
                class="sync-after-draft-dialog__btn sync-after-draft-dialog__btn--secondary"
                (click)="onSyncAfterDraftDecline()"
            >
                <span>Not now</span>
            </button>
            <button
                type="button"
                class="sync-after-draft-dialog__btn sync-after-draft-dialog__btn--primary"
                (click)="onSyncAfterDraftAccept()"
            >
                <i class="pi pi-cloud-upload" aria-hidden="true"></i>
                <span>Sync to M3</span>
            </button>
        </div>
    </ng-template>
</p-dialog>

<p-toast></p-toast>
<p-toast position="top-right" key="custom-toast" [baseZIndex]="5000">
    <ng-template let-message pTemplate="message">
        <div class="flex flex-col items-start flex-auto">
            <div class="font-bold text-xl mb-2">{{ message.summary }}</div>
            <div class="font-medium text-lg">{{ message.detail }}</div>
        </div>
    </ng-template>
</p-toast>



